<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import TaskExecutionsTab from './TaskExecutionsTab.vue';
import TaskListTab from './TaskListTab.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import CreateTaskDialog from './create/CreateTaskDialog.vue';

const { t } = useI18n();

const tabs = computed(() => [
  {
    key: 'tasks',
    name: t('WGPT_TASKS.TABS.TASKS.NAME'),
  },
  {
    key: 'executions',
    name: t('WGPT_TASKS.TABS.EXECUTIONS.NAME'),
  },
]);

const tabBarTabs = computed(() =>
  (tabs.value || []).map(tab => ({ label: tab.name, key: tab.key }))
);
const activeTab = ref('tasks');
const searchQuery = ref('');

const activeTabIndex = computed(() => {
  const idx = (tabs.value || []).findIndex(tab => tab.key === activeTab.value);
  return idx === -1 ? 0 : idx;
});

const onTabChange = tab => {
  // TabBar emits the tab object, not index
  activeTab.value = tab.key;
  searchQuery.value = '';
};

const createTaskDialogRef = ref(null);
const executionsTabRef = ref(null);
const openCreateTask = () => {
  createTaskDialogRef.value.open();
};
const refreshExecutions = () => {
  executionsTabRef.value?.refresh();
};
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('WGPT_TASKS.HEADER')"
        :description="t('WGPT_TASKS.DESCRIPTION')"
        :search-placeholder="
          activeTab === 'tasks'
            ? t('WGPT_TASKS.SEARCH_PLACEHOLDER')
            : t('WGPT_TASKS.EXECUTIONS.SEARCH_PLACEHOLDER')
        "
        feature-name="wgpt_tasks"
      >
        <template #tabs>
          <TabBar
            :tabs="tabBarTabs"
            :initial-active-tab="activeTabIndex"
            @tab-changed="onTabChange"
          />
        </template>
        <template #actions>
          <Button
            v-if="activeTab === 'tasks'"
            :label="t('WGPT_TASKS.HEADER_BTN_TEXTS.CREATE')"
            size="sm"
            @click="openCreateTask"
          />
          <Button
            v-if="activeTab === 'executions'"
            :label="t('WGPT_TASKS.EXECUTIONS.REFRESH')"
            icon="i-lucide-refresh-cw"
            size="sm"
            slate
            @click="refreshExecutions"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <section class="flex-1 overflow-auto mt-4">
        <TaskExecutionsTab
          v-if="activeTab === 'executions'"
          ref="executionsTabRef"
          :search-query="searchQuery"
        />
        <TaskListTab v-else :search-query="searchQuery" />
      </section>
      <CreateTaskDialog ref="createTaskDialogRef" />
    </template>
  </SettingsLayout>
</template>
